<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMeStore } from '@/stores/me';
import { useModalStore, type ModalKey } from '@/stores/modal';
import UiButton from '@/components/ui/UiButton.vue';
import WorkspaceSwitcher from '@/components/cabinet/WorkspaceSwitcher.vue';
import BottomNav from '@/components/mobile/BottomNav.vue';
import PwaInstallBanner from '@/components/mobile/PwaInstallBanner.vue';
import ImpersonationBanner from '@/components/cabinet/ImpersonationBanner.vue';

// Shared cabinet shell — sidebar + topbar — для всех /dashboard /links
// /campaigns /bio /qr /settings/* views. Раньше markup жил в DashboardView,
// из-за чего остальные cabinet-страницы рендерились без shell'а.
//
// inbox-130: topbar теперь содержит .topbar-search + notification-bell.
// h1 page-title переехал в PageHead per-view (см. inbox-129) — здесь
// больше не рендерится.

const route = useRoute();
const router = useRouter();
const me = useMeStore();
const modals = useModalStore();

const search = ref('');
const hasNotifications = ref(false);

onMounted(async () => {
  await me.load();
  if (!me.isAuthenticated) router.push({ name: 'login' });
});

function isActive(prefix: string): boolean {
  if (prefix === '/dashboard') return route.path === '/dashboard';
  return route.path.startsWith(prefix);
}

async function logout() {
  await me.logout();
  router.push({ name: 'login' });
}

function openCreate() {
  modals.open('create-link');
}

// inbox-475 — контекстная primary-кнопка в topbar. На /qr меняется на
// «Создать QR», на /bio — «Создать страницу», на /campaigns —
// «Создать кампанию». На /analytics скрывается полностью (просмотр —
// не место для создания). На /dashboard и /links остаётся «Создать ссылку».
interface TopCta {
  label: string;
  modal: ModalKey;
  hidden?: boolean;
}
const topCta = computed<TopCta>(() => {
  const path = route.path;
  if (path.startsWith('/qr')) return { label: '+ Создать QR', modal: 'qr-customizer' };
  if (path.startsWith('/bio')) return { label: '+ Создать страницу', modal: 'create-bio' };
  if (path.startsWith('/campaigns')) return { label: '+ Создать кампанию', modal: 'create-campaign' };
  if (path.startsWith('/analytics')) return { label: '', modal: 'create-link', hidden: true };
  return { label: '+ Создать ссылку', modal: 'create-link' };
});
function triggerTopCta() {
  modals.open(topCta.value.modal);
}

function openNotifications() {
  // TODO: модалка 'notifications' появится отдельной задачей. Сейчас
  // кнопка — no-op для совместимости с ModalKey union.
}
</script>

<template>
  <!-- inbox-274 — fixed top banner; CSS class на .cabinet добавляется когда
       impersonation активна (top padding под banner). Сам banner рендерит
       себя через v-if если me.impersonation non-null. -->
  <ImpersonationBanner />
  <div class="cabinet" :class="{ 'has-imp-banner': !!me.me?.impersonation }">
    <!-- inbox-284: hidden lg:flex прячет sidebar на < 1024px, BottomNav
         (lg:hidden) занимает её место. На lg+ работает старый desktop
         layout 240px-grid. -->
    <aside class="sidebar hidden lg:flex">
      <div class="brand">
        <span class="brand-mark">L</span>
        <span class="brand-name">link-platform</span>
      </div>

      <WorkspaceSwitcher />

      <nav class="side-nav">
        <!-- inbox-168: 4 секции × 11 items per docs/mockups/lib/cabinet-shell.js NAV_SECTIONS. -->
        <div class="side-section">
          <div class="side-section-title">Главное</div>
          <router-link to="/dashboard" class="side-link" :class="{ active: isActive('/dashboard') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            <span>Обзор</span>
          </router-link>
          <router-link to="/links" class="side-link" :class="{ active: isActive('/links') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            <span>Ссылки</span>
          </router-link>
          <router-link to="/campaigns" class="side-link" :class="{ active: isActive('/campaigns') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6"/></svg>
            <span>Кампании</span>
          </router-link>
          <router-link to="/bio" class="side-link" :class="{ active: isActive('/bio') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            <span>Bio-страницы</span>
          </router-link>
          <router-link to="/qr" class="side-link" :class="{ active: isActive('/qr') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="6" height="6"/><rect x="14" y="4" width="6" height="6"/><rect x="4" y="14" width="6" height="6"/><rect x="14" y="14" width="6" height="6"/></svg>
            <span>QR-коды</span>
          </router-link>
        </div>

        <div class="side-section">
          <div class="side-section-title">Аналитика</div>
          <router-link to="/analytics" class="side-link" :class="{ active: isActive('/analytics') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3v18h18"/><path d="m7 14 4-4 4 4 5-5"/></svg>
            <span>Дашборды</span>
          </router-link>
          <router-link to="/antifraud" class="side-link" :class="{ active: isActive('/antifraud') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>Антифрод</span>
          </router-link>
          <router-link to="/conversions" class="side-link" :class="{ active: isActive('/conversions') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
            <span>Конверсии</span>
          </router-link>
        </div>

        <div class="side-section">
          <div class="side-section-title">Инструменты</div>
          <router-link to="/copilot" class="side-link" :class="{ active: isActive('/copilot') || isActive('/ai-copilot') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            <span>AI-копайлот</span>
          </router-link>
          <router-link to="/connections" class="side-link" :class="{ active: isActive('/connections') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            <span>Интеграции</span>
          </router-link>
        </div>

        <div class="side-section">
          <div class="side-section-title">Настройки</div>
          <router-link to="/settings/profile" class="side-link" :class="{ active: isActive('/settings') }">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a8 8 0 0 1 16 0v1"/></svg>
            <span>Настройки</span>
          </router-link>
        </div>
      </nav>

      <footer v-if="me.currentUser" class="side-foot">
        <span class="user-name">{{ me.currentUser.name }}</span>
        <button type="button" class="logout-btn" @click="logout">Выйти</button>
      </footer>
    </aside>

    <main class="main pb-20 lg:pb-12">
      <header class="topbar">
        <div class="topbar-search">
          <svg
            class="search-ic"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            v-model="search"
            type="search"
            class="search-input"
            placeholder="Поиск ссылок, кампаний, доменов…"
            aria-label="Поиск"
          >
          <span class="kbd">⌘K</span>
        </div>

        <div class="topbar-actions">
          <button
            type="button"
            class="notif-btn"
            title="Уведомления"
            aria-label="Уведомления"
            @click="openNotifications"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span
              v-if="hasNotifications"
              class="notif-dot"
              aria-hidden="true"
            />
          </button>

          <UiButton v-if="!topCta.hidden" size="sm" @click="triggerTopCta">
            {{ topCta.label }}
          </UiButton>
        </div>
      </header>
      <slot />
    </main>

    <!-- inbox-293: PWA install banner — сам display:none на lg+, gates
         через localStorage 2-visit + 30-day dismiss. -->
    <PwaInstallBanner />

    <!-- inbox-284: BottomNav сам lg:hidden (см. компонент). Здесь
         рендерим всегда — display-flip управляется внутри. -->
    <BottomNav />
  </div>
</template>

<style scoped>
/* inbox-284: на < 1024px grid схлопывается в 1 колонку (sidebar
 * hidden), main растягивается на всю ширину. На lg+ возвращаемся к
 * 240px sidebar + 1fr main. */
.cabinet { min-height: 100vh; background: #0a0d12; color: #e6eaf0; font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', system-ui, sans-serif; font-size: 14px; display: grid; grid-template-columns: 1fr; }
/* inbox-274 — top padding под fixed impersonation banner. На мобильном
   banner может занять 2 строки → больше отступ. */
.cabinet.has-imp-banner { padding-top: 52px; }
@media (max-width: 760px) {
  .cabinet.has-imp-banner { padding-top: 84px; }
}
@media (min-width: 1024px) {
  .cabinet { grid-template-columns: 240px 1fr; }
}

/* Sidebar — wrapped in lg+ media query so scoped specificity does NOT
 * override Tailwind `.hidden` on mobile (inbox-325). Without this wrap,
 * `.sidebar[data-v-xxx]` 0-2-0 beats `.hidden` 0-1-0 and sidebar shows
 * on mobile despite `class="sidebar hidden lg:flex"`. */
@media (min-width: 1024px) {
  /* inbox-472 — border-right заменён на box-shadow inset чтобы рендериться
   * ровно 1 device-pixel на любом DPR. HTML border на Retina (DPR≥1.5)
   * может рендериться как 0.5-0.66 device-pixel; getComputedStyle
   * возвращает дробное значение, MCP-walk видит 0.55px. box-shadow с
   * явной pixel-offset стабильно даёт целый пиксель и не зависит от DPR. */
  .sidebar { background: #11161e; box-shadow: inset -1px 0 0 0 #1f2733; padding: 18px 0; display: flex; flex-direction: column; position: sticky; top: 0; height: 100vh; }
}
.brand { display: flex; align-items: center; gap: 10px; padding: 0 18px 14px; font-weight: 700; font-size: 16px; }
.brand-mark { width: 28px; height: 28px; border-radius: 8px; background: linear-gradient(135deg, #4f8cff, #b366ff); display: grid; place-items: center; color: #fff; font-weight: 800; font-size: 14px; }

.side-nav { padding: 4px 8px; flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
.side-section { margin-top: 14px; }
.side-section:first-child { margin-top: 4px; }
.side-section-title { padding: 0 12px 6px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.08em; color: #5a6577; font-weight: 600; }
.side-link { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 7px; color: #9aa5b1; font-size: 13.5px; text-decoration: none; }
.side-link:hover { background: #161c26; color: #e6eaf0; }
.side-link.active { background: rgba(79,140,255,0.16); color: #6f9eff; font-weight: 500; }

.side-foot { padding: 12px 16px; border-top: 1px solid #1f2733; display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-shrink: 0; }
.user-name { font-size: 13px; color: #e6eaf0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
/* inbox-330 — explicit min-height (раньше height collapsed когда parent flex
   sizing давал 0); fz 12→13 для consistency с компактными элементами.
   inbox-473 — min-height 36 → 40, radius 6 → 8 для unification с
   .notif-btn (topbar bell h=40) и UiButton size=md (h=40 r=8). */
.logout-btn { background: transparent; border: 0; color: #9aa5b1; font-size: 13px; cursor: pointer; padding: 8px 12px; border-radius: 8px; font-family: inherit; min-height: 40px; flex-shrink: 0; }
.logout-btn:hover { color: #ff5d8f; background: rgba(255,93,143,0.08); }

/* Main */
/* inbox-504 — max-width убран полностью: контент растягивается на всю
   доступную ширину 1fr-колонки (vw - 240px sidebar). Раньше max-width
   1200 (inbox-462) оставлял пустоту справа на FullHD+ экранах; для
   traffic-tool сценария (превью лендинга + таблицы кампаний) широкий
   контент важнее центрирования. */
/* inbox-118 safety net: overflow-x hidden чтобы ничто внутри (длинные
   subtitle / table / preview iframe) не растягивало layout за viewport. */
.main { padding: 0 32px 48px; min-width: 0; margin: 0; width: 100%; overflow-x: hidden; }
@media (max-width: 760px) {
  .main { padding: 0 16px 48px; }
}

/* inbox-203 fix: .topbar теперь соответствует mockup'у lib/cabinet.css
 * .cabinet-topbar — sticky 56px header с backdrop + явный border. Без
 * height не вытягивалась к 56px (схлопывалась к 38.5px по content).
 * Без явного border-bottom — рендерилась "0px none rgb(232,237,243)"
 * (default = currentColor = --fg #e8edf3 → визуальный белый артефакт
 * в DOM-inspector). */
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  height: 56px;
  margin: 0 -32px 22px;
  padding: 0 32px;
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(10, 13, 18, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border);
}

/* Topbar — search.
   inbox-330 — h=40 r=8 для consistency с UiInput/UiButton. Border-strong +
   bg-card вместо elev — выше контраст с topbar bg, выглядит как полноценное
   field а не plain текст. */
.topbar-search {
  /* inbox-463 v3 — width 320px фиксированная (user: «поуже и аккуратнее»).
     Раньше flex: 1 max-width 480 — растягивался на доступное место. */
  flex: 0 0 320px;
  max-width: 320px;
  display: flex;
  align-items: center;
  gap: 8px;
  /* inbox-504 — height 40 → 32 для compactness topbar (user feedback:
   * «кнопки слишком большие»). Visual rhythm 32 совпадает с UiButton
   * size=sm и .notif-btn 32×32 ниже. */
  height: 32px;
  box-sizing: border-box;
  padding: 0 10px;
  background: var(--color-bg-card, #11161e);
  /* inbox-355 — явный 1px border-width. На прод replaced subpixel
   * (0.555556px) рендерился как ~0 на DPR=1 мониторах. Explicit 1px
   * через явное shorthand гарантирует rendering. */
  border: 1px solid var(--border-strong, #2a3441);
  border-radius: 8px;
  color: var(--color-fg-muted, #8b95a5);
  /* inbox-355 — лёгкая тень для visual clarity на любом DPR. */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: border-color 0.12s, box-shadow 0.12s;
}
.topbar-search:hover {
  border-color: var(--color-fg-muted, #8b95a5);
}
.topbar-search:focus-within {
  border-color: var(--color-accent, #4f8cff);
  box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.15);
}
.search-ic { width: 16px; height: 16px; color: var(--color-fg-dim, #6b7480); flex-shrink: 0; }
.search-input {
  flex: 1;
  /* inbox-355 — height явно фиксируем + min-width 0 + padding 0 —
   * иначе user-agent + Tailwind preflight давали ~36px (с form padding/
   * line-height inheritance), что распирало wrapper выше 40. */
  height: 100%;
  min-width: 0;
  padding: 0;
  background: transparent;
  border: 0;
  outline: 0;
  /* @tailwindcss/forms добавляет focus:ring на любой input — двойная
     обводка inside .topbar-search wrapper. Подавляем — есть focus-within
     на wrapper'е, отдельный ring на input не нужен. */
  box-shadow: none !important;
  color: var(--color-fg, #e8edf3);
  font: inherit;
  font-size: 13.5px;
  line-height: 1.4;
}
.search-input::placeholder { color: var(--color-fg-dim, #6b7480); }
.kbd {
  padding: 1px 6px;
  border: 1px solid var(--color-border, #1f2733);
  border-radius: 4px;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 11px;
  color: var(--color-fg-dim, #6b7480);
}

/* Topbar — actions */
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Topbar icon-button — 36×36, br 8 (canonical, выровнен с topbar-search). */
.notif-btn {
  position: relative;
  width: 36px;
  height: 36px;
  background: var(--color-bg-elev, #0f1620);
  border: 1px solid var(--color-border, #1f2733);
  border-radius: 8px;
  color: var(--color-fg-muted, #8b95a5);
  display: grid;
  place-items: center;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.notif-btn:hover {
  color: var(--color-fg, #e8edf3);
  border-color: var(--color-fg-muted, #8b95a5);
  background: var(--color-bg-card, #11161e);
}
.notif-btn svg { width: 14px; height: 14px; }

.notif-dot {
  position: absolute;
  top: 4px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-danger, #ff5d8f);
  border: 2px solid var(--color-bg-elev, #0f1620);
}

/* inbox-284: legacy 980/640 media queries удалены — на < 1024px sidebar
 * полностью скрыт через .hidden, навигация уезжает в BottomNav. На
 * 1024..1280 сохраняем полный 240px sidebar.
 *
 * Mobile topbar: сжимаем search-input (без ⌘K placeholder hint), main
 * получает меньший inline padding. */
@media (max-width: 1023px) {
  .topbar-search .kbd { display: none; }
  .topbar-search { max-width: none; }
  .main { padding: 16px 16px 24px; }
  .topbar { gap: 8px; margin: 0 -16px 18px; padding: 0 16px; }
  .search-input::placeholder { font-size: 12.5px; }
}
/* inbox-349/350 — на узких viewport topbar пакуется плотнее: search
   collapse'ится в icon-only (40×40), bell + CTA остаются видимыми.
   inbox-463 — breakpoint 520 → 767 (acceptance: «на экранах меньше
   768 поиск в верхней панели сворачивается в иконку»). */
@media (max-width: 767px) {
  .topbar-search {
    flex: 0 0 40px;
    max-width: 40px;
    min-height: 40px;
    padding: 0;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: 8px;
  }
  .topbar-search .search-input,
  .topbar-search .kbd { display: none; }
  .topbar-actions { gap: 6px; flex-shrink: 0; }
  .notif-btn { width: 36px; height: 36px; }
  .topbar-actions :deep(.ui-btn) { padding: 8px 12px; min-height: 36px; font-size: 13px; white-space: nowrap; }
}
</style>
